import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { BadgeCheck, TrendingUp } from "lucide-react";
import { fmtDate, EMDASH } from "./statFormat";

const CONF = {
  high: { c: "bg-emerald-500", t: "High" }, medium: { c: "bg-amber-500", t: "Medium" }, low: { c: "bg-slate-400", t: "Low" },
};
const COMP_LABEL = {
  production: "Production", avg: "Average", discipline: "Plate Discipline", power: "Power",
  run_production: "Run Production", baserunning: "Baserunning",
  run_prevention: "Run Prevention", baserunner_prevention: "Baserunner Prev.", strikeout: "Strikeout",
  command: "Command", suppression: "Contact Suppression", outcomes: "Outcomes",
  fielding_pct: "Fielding %", activity: "Activity", difficulty: "Position Difficulty",
};

const rk = (v) => (v == null ? EMDASH : `#${v}`);
const sc = (v) => (v == null ? EMDASH : Number(v).toFixed(1));

/** Phase 1D — activates the reserved leadership module. Renders only when the
 * player has qualified, approved-data-derived scores. */
export default function PlayerInGameScore({ playerId }) {
  const [d, setD] = useState(undefined);
  useEffect(() => {
    api.get(`/players/${playerId}/ingame-score`).then((r) => setD(r.data)).catch(() => setD(null));
  }, [playerId]);

  if (d === undefined) return null;
  if (!d || !d.has_data) return null; // neutral empty state (hidden) until qualified

  const conf = CONF[d.confidence] || CONF.low;
  const s = d.sample || {};
  const ranks = d.ranks || {};
  const cp = d.component_percentiles || {};
  const scoreRows = [
    ["Overall", d.overall_score, true],
    ["Hitting", d.hitting_score],
    ["Pitching", d.pitching_score],
    ["Fielding", d.fielding_score],
    ...(d.two_way_score != null ? [["Two-Way", d.two_way_score]] : []),
  ].filter(([, v]) => v != null);

  // pick the dominant component section for breakdown
  const section = d.hitting_score != null ? "hitting" : d.pitching_score != null ? "pitching" : "fielding";
  const breakdown = Object.entries(cp[section] || {});

  return (
    <div data-testid="ingame-score-card" className="border border-[#0A192F] bg-[#0A192F] text-white rounded-sm overflow-hidden">
      <div className="px-6 pt-5 pb-4">
        <div className="flex items-center gap-2 label-overline text-[#E63946]"><TrendingUp className="w-4 h-4" /> CBR In-Game Performance</div>
        <div className="flex items-end gap-3 mt-2">
          <div className="font-data text-5xl leading-none">{sc(d.overall_score)}</div>
          <div className="pb-1">
            <span className={`inline-block text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm ${conf.c}`} data-testid="ingame-confidence">{conf.t} Confidence</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mt-4">
          {scoreRows.slice(1).map(([label, v]) => (
            <div key={label} className="flex items-center justify-between text-sm">
              <span className="text-white/60">{label}</span><span className="font-data">{sc(v)}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-[#0d2440] px-6 py-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <Rank label="National" v={ranks.national?.rank} pop={ranks.national?.pop} highlight />
        {ranks.provincial && <Rank label={`${ranks.provincial.province} Prov.`} v={ranks.provincial.rank} pop={ranks.provincial.pop} />}
        {ranks.graduation_year && <Rank label={`Class ${ranks.graduation_year.year}`} v={ranks.graduation_year.rank} pop={ranks.graduation_year.pop} />}
        {ranks.position && <Rank label={`${ranks.position.position}`} v={ranks.position.rank} pop={ranks.position.pop} />}
      </div>

      {breakdown.length > 0 && (
        <div className="px-6 py-4 border-t border-white/10">
          <div className="label-overline text-white/40 mb-2">{section} breakdown (percentile in class)</div>
          <div className="space-y-1.5">
            {breakdown.map(([k, v]) => (
              <div key={k} className="flex items-center gap-2">
                <span className="text-xs text-white/60 w-28 shrink-0">{COMP_LABEL[k] || k}</span>
                <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-[#E63946]" style={{ width: `${v || 0}%` }} /></div>
                <span className="font-data text-xs w-10 text-right">{v == null ? EMDASH : `${Math.round(v)}%`}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="px-6 py-3 border-t border-white/10 text-[11px] text-white/50 flex flex-wrap gap-x-3 gap-y-1">
        <span className="inline-flex items-center gap-1 text-emerald-300 font-semibold"><BadgeCheck className="w-3.5 h-3.5" /> Based on Approved CBR Game Data</span>
        <span>{s.games || 0}G</span>
        {s.pa ? <span>{s.pa} PA</span> : null}
        {s.ip ? <span>{s.ip} IP</span> : null}
        {s.fielding_chances ? <span>{s.fielding_chances} chances</span> : null}
        <span>{s.opponents || 0} opp · {s.tournaments || 0} tourn</span>
        {d.calculated_at && <span>Updated {fmtDate(d.calculated_at.slice(0, 10))}</span>}
      </div>
    </div>
  );
}

function Rank({ label, v, pop, highlight }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-white/60 text-xs">{label}</span>
      <span className={`font-data ${highlight ? "text-xl text-[#E63946]" : "text-base"}`}>{rk(v)}{pop ? <span className="text-[10px] text-white/40 ml-1">/{pop}</span> : null}</span>
    </div>
  );
}
